/**
 * Chapters of the guide (docs/guide/<locale>/<file>.md) and the actions that point into them.
 * Each topic's short text is the i18n key `help.<id>`; its anchor is a heading `{#<anchor>}` of
 * the chapter. A unit test checks that every anchor exists in both languages.
 */
export const HELP_CHAPTERS = [
  "ventes",
  "shopify",
  "expeditions",
  "stock",
  "sav",
  "achats",
  "catalogue",
  "comptabilite",
  "automatismes",
  "reglages",
  "statuts",
] as const;
export type HelpChapter = (typeof HELP_CHAPTERS)[number];

const topics = {
  ventes: [
    "quote.preview",
    "quote.send",
    "quote.accept",
    "quote.invoice",
    "quote.duplicate",
    "quote.decline",
    "order.prepare",
    "order.deliveryNote",
    "order.urgent",
    "order.allocate",
    "order.delivered",
    "order.cancel",
    "order.archive",
    "order.invoice",
    "order.deposit",
    "order.publicRefs",
    "invoice.issue",
    "invoice.deleteDraft",
    "invoice.payment",
    "invoice.creditNote",
    "invoice.send",
    "invoice.pdf",
    "invoice.export",
    "invoice.external",
    "customer.importShopify",
    "customer.archive",
    "customer.history",
  ],
  shopify: ["shopify.openOrder", "shopify.sync", "shopify.webhooks", "shopify.writeBack", "shopify.webInvoiceMode"],
  expeditions: [
    "shipment.ship",
    "shipment.laposteFile",
    "shipment.parcelLaposteFile",
    "shipment.delivered",
    "shipment.tracking",
    "shipment.resend",
    "shipment.serials",
    "shipment.cancel",
    "shipment.free",
  ],
  stock: [
    "stock.adjust",
    "stock.exit",
    "stock.count",
    "stock.countValidate",
    "stock.countCancel",
    "stock.serials",
    "stock.composition",
  ],
  sav: [
    "sav.undo",
    "sav.returnLabel",
    "sav.laposteReturn",
    "sav.receive",
    "sav.replace",
    "sav.exchange",
    "sav.refund",
    "sav.resolve",
    "sav.close",
    "sav.reopen",
  ],
  achats: [
    "purchase.send",
    "purchase.close",
    "purchase.status",
    "purchase.edit",
    "purchase.receive",
    "purchase.payment",
    "purchase.cancel",
    "purchase.suggestions",
    "purchase.documents",
    "purchase.creditNote",
    "purchase.transmit",
  ],
  catalogue: ["product.importShopify", "product.split", "product.archive", "product.families", "product.webLocked"],
  comptabilite: ["accounting.chorus", "accounting.inqom", "accounting.inqomSandbox", "accounting.deposits"],
  automatismes: ["auto.jobs"],
  reglages: [
    "settings.integrationTest",
    "settings.archiveDelay",
    "settings.userRole",
    "settings.import",
    "settings.health",
    "settings.printing",
  ],
  statuts: ["statuses.all"],
} as const satisfies Record<HelpChapter, readonly string[]>;

type Topics = typeof topics;
export type HelpId = Topics[keyof Topics][number];

export const HELP_TOPICS = Object.fromEntries(
  (Object.entries(topics) as [HelpChapter, readonly HelpId[]][]).flatMap(([chapter, ids]) =>
    ids.map((id) => [id, { chapter, anchor: id.replace(".", "-") }]),
  ),
) as Record<HelpId, { chapter: HelpChapter; anchor: string }>;
